import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import {
  faCircleInfo,
  faExclamationCircle,
} from '@fortawesome/pro-solid-svg-icons';
import type { CSSProperties } from 'react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { styled } from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Vault } from '@sorare/core/src/atoms/icons/Vault';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LinkBox, LinkOverlay } from '@sorare/core/src/atoms/navigation/Box';
import { LabelL, LabelM } from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { getRarityColor } from '@sorare/core/src/lib/colors';
import { validateVaultRequirements } from '@sorare/core/src/lib/so5';

import { LeagueTrckVaultGatedHowItWorksDialogWithFetch } from 'components/leagueTrck/LeagueTrckVaultGatedHowItWorksDialog/index.withFetch';

import type { VaultPointsRuleBox_So5Leaderboard } from './__generated__/index.graphql';

const Box = styled(LinkBox)`
  padding: var(--intermediate-unit) var(--double-unit);
  border-radius: var(--double-unit);
  background: var(--c-nd-50);
`;

const BoxContent = styled(Vertical)`
  flex: 1;
`;

const WhistleContainer = styled(Horizontal).attrs({ gap: 0, center: true })`
  position: relative;
  aspect-ratio: 1;
  --icon-size: var(--double-and-a-half-unit);
  height: var(--icon-size);
  width: var(--icon-size);
  background: var(--c-icon-background);

  &:before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    background: var(--c-icon-background);
    border: 1px solid var(--c-icon, var(--c-white));
    transform: rotate(45deg);
  }
`;

const ExclamationIcon = styled(FontAwesomeIcon)`
  color: var(--c-yellow-400);
  position: absolute;
  top: 0;
  right: 0;
  transform: translate(25%, -25%);
`;

const VaultIcon = styled(Vault)`
  height: calc(var(--icon-size) - 2px) !important;
`;

type Props = {
  so5Leaderboard?: VaultPointsRuleBox_So5Leaderboard;
};

export const VaultPointsRuleBox = ({ so5Leaderboard }: Props) => {
  const [showHowItWorksDialog, setShowHowItWorksDialog] = useState(false);
  if (!so5Leaderboard) {
    return null;
  }
  const { isMissingVaultPoints, minVaultBoost } =
    validateVaultRequirements(so5Leaderboard);
  const minVaultLevel = minVaultBoost?.boostLevel?.milestone;

  if (!minVaultLevel) {
    return null;
  }

  const iconColor = so5Leaderboard.mainRarityType
    ? getRarityColor(so5Leaderboard.mainRarityType)
    : undefined;
  const iconBackgroundColor = isMissingVaultPoints
    ? 'var(--c-black)'
    : '#312300';
  return (
    <>
      <Box>
        <Horizontal gap={1.5} grow>
          <WhistleContainer
            style={
              {
                '--c-icon': isMissingVaultPoints
                  ? 'var(--c-nd-300-opaque)'
                  : iconColor,
                '--c-icon-background': iconBackgroundColor,
              } as CSSProperties
            }
          >
            <VaultIcon
              color={
                isMissingVaultPoints
                  ? 'var(--c-nd-300-opaque)'
                  : 'var(--c-icon)'
              }
            />
            {!!isMissingVaultPoints && (
              <ExclamationIcon icon={faExclamationCircle} size="sm" />
            )}
          </WhistleContainer>
          <BoxContent gap={0.5}>
            <LabelL bold>
              {isMissingVaultPoints ? (
                <FormattedMessage
                  id="FixtureCompetition.vaultGated.unlockCompetition"
                  defaultMessage="Unlock Competition"
                />
              ) : (
                <FormattedMessage
                  id="FixtureCompetition.vaultGated.title"
                  defaultMessage="Vault Competition"
                />
              )}
            </LabelL>
            <Ellipsis>
              <LabelM
                color={
                  isMissingVaultPoints
                    ? 'var(--c-yellow-400)'
                    : 'var(--c-onBase-success)'
                }
              >
                {isMissingVaultPoints ? (
                  <FormattedMessage
                    id="FixtureCompetition.vaultGated.requiresVaultLvl"
                    defaultMessage="Requires Vault Lvl {minVaultLevel}"
                    values={{
                      minVaultLevel,
                    }}
                  />
                ) : (
                  <FormattedMessage
                    id="FixtureCompetition.vaultGated.competitionUnlocked"
                    defaultMessage="Unlocked!"
                  />
                )}
              </LabelM>
            </Ellipsis>
          </BoxContent>
          <LinkOverlay
            as={ButtonBase}
            onClick={() => setShowHowItWorksDialog(true)}
          >
            <FontAwesomeIcon icon={faCircleInfo} />
          </LinkOverlay>
        </Horizontal>
      </Box>
      <LeagueTrckVaultGatedHowItWorksDialogWithFetch
        open={showHowItWorksDialog}
        rarity={so5Leaderboard.mainRarityType || Rarity.limited}
        leaderboardSlug={so5Leaderboard.slug}
        onClose={() => setShowHowItWorksDialog(false)}
      />
    </>
  );
};

VaultPointsRuleBox.fragments = {
  so5Leaderboard: gql`
    fragment VaultPointsRuleBox_So5Leaderboard on So5Leaderboard {
      slug
      mainRarityType
      ...validateVaultRequirements_so5Leaderboard
    }
    ${validateVaultRequirements.fragments.so5Leaderboard}
  ` as TypedDocumentNode<VaultPointsRuleBox_So5Leaderboard>,
};
